import type { GoogleAutocompletePlaceItem } from 'api/search'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ListSubheader from 'LuxKit/List/ListSubheader'
import usePlaceAutocomplete from 'hooks/usePlaceAutocomplete'
import noop from 'lib/function/noop'
import { useCallback } from 'react'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'

interface Props {
  searchPhrase?: string
  onSelect?: (option: GoogleAutocompletePlaceItem) => void
  selectedPlaceId?: string
  className?: string
  originLatitude?: number
  originLongitude?: number
  /**
   * Radius around the latitude/longitude given to return search results for in KM
   * Defaults to 100km
   * @default 100
   */
  radius?: number
  /**
   * Custom filter function to filter autocomplete predictions
   */
  filterPredictions?: (predictions: Array<GoogleAutocompletePlaceItem>) => Array<GoogleAutocompletePlaceItem>
}

function AddressInputOptions(props: Props) {
  const {
    className,
    searchPhrase = '',
    onSelect = noop,
    selectedPlaceId,
    originLatitude,
    originLongitude,
    radius = 100,
    filterPredictions,
  } = props

  const { isLoading, predictions } = usePlaceAutocomplete(searchPhrase, {
    latitude: originLatitude,
    longitude: originLongitude,
    // we accept in KM, but auto complete wants it in meters
    radius: radius * 1000,
    filterPredictions,
  })

  const noResultsFound = !isLoading && predictions.length === 0 && searchPhrase.length > 1
  const onItemSelect = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const placeId = e.currentTarget.dataset.placeid
    const option = predictions.find(p => p.placeId === placeId)
    if (option) onSelect(option)
  }, [onSelect, predictions])

  return <VerticalSpacer gap={12} className={className}>
    {!isLoading && predictions.length > 0 && <div>
      {predictions.map(option => (
        <ClickableListItem
          key={option.placeId}
          selected={option.placeId === selectedPlaceId}
          data-placeid={option.placeId}
          onClick={onItemSelect}
          size="medium"
          title={option.mainText}
          subtitle={option.secondaryText}
          startIcon={<LineMapMarkerIcon />}
        />
      ))}
    </div>}
    {!isLoading && noResultsFound && <div>
      <ListSubheader colour="light-foreground-alt">
        <FormattedMessage
          id="d8bR5CUtE9LfjF5zMfDA"
          defaultMessage="Sorry, we couldn’t find results for <b>''{searchPhrase}''</b>."
          description="Message shown when address search returns no results"
          values={{
            searchPhrase,
            b: (chunks) => <b>{chunks}</b>,
          }}
        />
      </ListSubheader>
      <ListSubheader colour="light-foreground-alt">
        <FormattedMessage
          id="LjhALI4C9lS4wgcNYfid"
          defaultMessage="Try a different search phrase"
          description="Suggestion shown below the no results message for address search"
        />
      </ListSubheader>
    </div>}
    {isLoading && <LoadingIndicator inline />}
  </VerticalSpacer>
}

export default AddressInputOptions
